import { styled } from '@mui/material';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import { chains } from '@/configs';

export const Root = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	marginTop: 40,
	maxWidth: 760,
	marginRight: 'auto',
	marginLeft: 'auto',
	padding: '0 20px 60px',
	width: '100%',
});

export const Card = styled(Box)(({ theme }) => ({
	border: `1px solid ${theme.palette.common.borderPrimary}`,
	borderRadius: 12,
	overflow: 'hidden',
}));

export const Row = styled(Box, {
	shouldForwardProp: (p: PropertyKey) => p !== 'divider',
})<{ divider?: boolean }>(({ theme, divider }) => ({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	gap: 12,
	padding: '12px 16px',
	borderBottom: divider ? `1px solid ${theme.palette.common.borderPrimary}` : undefined,
}));

export const Muted = styled(Typography)(({ theme }) => ({
	color: theme.palette.common.N150,
}));

export const Mono = styled(Typography)({
	fontFamily: 'monospace',
});

export const ChainLogo = styled('img')({
	width: 18,
	height: 18,
	borderRadius: 9,
	flexShrink: 0,
});

export const Pill = styled(Box, {
	shouldForwardProp: (p: PropertyKey) => p !== 'tone',
})<{ tone?: 'ok' | 'warn' | 'neutral' }>(({ theme, tone }) => {
	const colors = {
		ok: { fg: '#3ecf8e', bg: 'rgba(62,207,142,0.12)' },
		warn: { fg: '#f0b429', bg: 'rgba(240,180,41,0.12)' },
		neutral: { fg: theme.palette.common.N150, bg: theme.palette.common.backgroundSecondary },
	}[tone || 'neutral'];
	return {
		display: 'inline-flex',
		alignItems: 'center',
		padding: '2px 8px',
		borderRadius: 10,
		fontSize: 11,
		lineHeight: '16px',
		whiteSpace: 'nowrap',
		color: colors.fg,
		backgroundColor: colors.bg,
	};
});

export const Pager = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 12,
	marginTop: 12,
	width: '100%',
});

// The dashboard API speaks wormhole chain ids; the interface configs are keyed
// by them too, so this is the single translation point for names and logos.
export function chainByWormholeId(chainId: number) {
	return chains.find((c) => c.wChainId === chainId);
}

export function chainLabel(chainId: number): string {
	return chainByWormholeId(chainId)?.chainName ?? `Chain ${chainId}`;
}

export function explorerTxUrl(chainId: number, txHash: string): string | null {
	const chain = chainByWormholeId(chainId);
	if (!chain) {
		return null;
	}
	return chain.mode === 'SOLANA' ? `${chain.blockExplorer}/tx/${txHash}` : `${chain.blockExplorer}/tx/${txHash}`;
}

export function shortHash(value: string, lead = 6, tail = 4): string {
	return value.length <= lead + tail ? value : `${value.slice(0, lead)}…${value.slice(-tail)}`;
}

export function formatDate(iso: string): string {
	const d = new Date(iso);
	return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}
